/**
 * Textos da ajuda contextual (drawer) das telas de Permissões / Rotas.
 * Mesmo formato de painel que o módulo PCO (recursosPcoHelpCopy).
 */

import type { PcoHelpContent } from '@/config/resourcesPcoHelpCopy';

export const PERMISSIONS_HELP_PAGE_KEYS = ['routes', 'users', 'user-edit', 'custom-groups', 'matrix'] as const;

export type PermissionsHelpPageKey = (typeof PERMISSIONS_HELP_PAGE_KEYS)[number];

const permissionsHelpByPage: Record<PermissionsHelpPageKey, PcoHelpContent> = {
    routes: {
        panelTitle: 'Gerenciamento de rotas',
        intro:
            'Aqui estão listadas todas as páginas do sistema. O banco de dados é a fonte única de controle de acesso — o que estiver configurado aqui define o que cada usuário vê no menu e pode acessar.',
        sections: [
            {
                title: 'Para cada rota você pode configurar',
                bullets: [
                    'Na lista, use os interrupts da coluna Status — cada alteração em Ativa, Requer login ou Só Admin Master é salva na hora',
                    'Grupos do AD e grupos personalizados — botão Grupos abre um diálogo; confirme com Salvar grupos'
                ]
            },
            {
                title: 'Área do sistema',
                paragraph:
                    'Use as abas acima da tabela para filtrar por módulo (Home, Colaboradores, Reuniões, PCO, ITAM etc.), alinhado ao menu lateral. Dentro da aba, a busca e a paginação seguem válidas.'
            },
            {
                title: 'Hierarquia de acesso',
                bullets: [
                    'Admin Master — acesso total',
                    'Nível individual — sobrepõe tudo abaixo',
                    'Grupos do AD — nível membro se pertence ao grupo',
                    'Padrão — membro se sem restrição'
                ]
            },
            {
                title: 'Sincronizar rotas',
                paragraph:
                    'O botão abaixo do breadcrumb envia as rotas definidas no router do frontend para o banco, criando ou atualizando registros. Use após incluir novas páginas ou alterar paths no código.'
            }
        ],
        alerts: [
            {
                type: 'info',
                text: 'Se uma rota não tem grupos configurados, todos os usuários autenticados podem acessar.'
            },
            {
                type: 'warning',
                text: 'Se um item não aparece no menu do usuário, ele não tem acesso à rota no banco.'
            }
        ]
    },
    users: {
        panelTitle: 'Gerenciamento de usuários',
        intro: 'Lista todos os usuários do sistema com estatísticas de quantas rotas cada um pode acessar.',
        sections: [
            {
                title: 'Ações disponíveis',
                bullets: [
                    'Editar — alterar dados e status do usuário',
                    'Ver detalhes — estatísticas, grupos e rotas acessíveis',
                    'Gerenciar rotas — definir nível em cada rota individualmente',
                    'Ocultar no diretório (Admin Master) — no modal de edição, o utilizador deixa de aparecer na lista Colaboradores e em listagens que usam a mesma API; o perfil público para terceiros deixa de estar disponível'
                ]
            },
            {
                title: 'Níveis de acesso por rota',
                bullets: [
                    'Admin — gerencia configurações da rota',
                    'Editor — edita conteúdo',
                    'Membro — somente visualização',
                    'Sem acesso — bloqueado'
                ]
            },
            {
                title: 'Sincronizar com o diretório',
                paragraph:
                    'O botão abaixo do breadcrumb atualiza a lista de usuários a partir do diretório. Use quando precisar refletir mudanças de contas ou grupos no AD.'
            },
            {
                title: 'Prestadores externos',
                paragraph:
                    'Usuários externos ficam com status "Aguardando aprovação" até que um admin os ative. Use o filtro de status para encontrá-los rapidamente.'
            }
        ],
        alerts: [
            {
                type: 'warning',
                text: 'O nível individual sempre sobrepõe o acesso definido por grupo do AD.'
            }
        ]
    },
    'user-edit': {
        panelTitle: 'Permissões por usuário',
        intro:
            'Nesta tela você define o nível de acesso deste usuário em cada rota do sistema (membro, editor, admin ou sem acesso), independentemente dos grupos do AD.',
        sections: [
            {
                title: 'Como usar',
                bullets: [
                    'Use a busca para filtrar rotas por caminho, nome ou título.',
                    'Abra o menu do nível atual e escolha o novo nível; a alteração é salva por rota.',
                    'Rotas "Admin only" (Admin Master) aparecem destacadas — apenas administradores globais podem usá-las.'
                ]
            }
        ],
        alerts: [
            {
                type: 'info',
                text: 'Níveis definidos aqui têm prioridade sobre o acesso herdado por grupos.'
            }
        ]
    },
    'custom-groups': {
        panelTitle: 'Grupos personalizados',
        intro:
            'Além dos grupos do Active Directory, você pode criar grupos personalizados para organizar usuários com necessidades específicas de acesso.',
        sections: [
            {
                title: 'Como usar',
                bullets: [
                    'Clique em "Novo grupo" para criar; informe nome, descrição e o nível de acesso (Membro, Editor ou Admin).',
                    'O nível vale para TODAS as rotas vinculadas ao grupo: todos os membros o herdam automaticamente, sem configuração individual.',
                    'Adicione membros pelo ícone de conta.',
                    'Associe o grupo às rotas na tela de Rotas (grupos personalizados aparecem na lista de grupos permitidos).',
                    'Se um usuário pertence a mais de um grupo da mesma rota, vale o maior nível; ajustes individuais na tela de Usuários continuam prevalecendo sobre o grupo.'
                ]
            },
            {
                title: 'Quando usar grupos personalizados',
                bullets: [
                    'Equipes temporárias — projetos com prazo definido',
                    'Acessos especiais — que não correspondem à estrutura do AD',
                    'Combinações — pessoas de diferentes departamentos'
                ]
            },
            {
                title: 'Ações por grupo',
                bullets: [
                    'Gerenciar membros, editar nome/descrição, ativar/desativar e excluir permanentemente conforme os ícones na tabela.'
                ]
            }
        ],
        alerts: [
            {
                type: 'info',
                text: 'Membros do grupo herdam o nível configurado no grupo (Membro, Editor ou Admin) nas rotas associadas; ajustes individuais feitos por usuário continuam prevalecendo.'
            }
        ]
    },
    matrix: {
        panelTitle: 'Matriz de permissões',
        intro:
            'Visão comparativa que mostra os níveis de acesso de vários usuários lado a lado em todas as rotas do sistema.',
        sections: [
            {
                title: 'Como usar',
                bullets: [
                    'Selecione até 8 usuários no campo de seleção.',
                    'Clique em "Carregar matriz".',
                    'Use os filtros de módulo para focar em uma área.',
                    'Clique em qualquer chip de nível para alterar diretamente.'
                ]
            },
            {
                title: 'Ideal para',
                bullets: [
                    'Comparar permissões entre colaboradores',
                    'Identificar diferenças de acesso entre equipes',
                    'Ajustar níveis sem navegar entre telas',
                    'Auditar acesso a áreas sensíveis'
                ]
            },
            {
                title: 'Legenda rápida',
                bullets: [
                    'Admin — acesso total à rota',
                    'Editor — edita conteúdo',
                    'Membro — visualização',
                    'Sem acesso — bloqueado'
                ]
            }
        ],
        alerts: [
            {
                type: 'info',
                text: 'Rotas restritas a Admin Master aparecem com destaque na grade.'
            }
        ]
    }
};

export function getPermissionsHelpContent(key: PermissionsHelpPageKey): PcoHelpContent {
    return permissionsHelpByPage[key];
}
